import SubframeCore, { IconName } from "@subframe/core";
import { Recipient } from "../redux/interfaces/reducersInterfaces";
import { Message } from "../Interfaces/messagesInterface";
import moment from "moment";
import { getCountryCodeFromNumber } from "../Utils/countryNameToCode";
import { formatPhoneNumber } from "../Utils/formatPhoneNumber";

interface RecipientProps {
  recipient: Recipient;
  lastMessage: Message;
}

function Friend({ recipient, lastMessage }: RecipientProps) {
  const countryCode = recipient && getCountryCodeFromNumber(recipient?.phone_number.toString());
  
  // Safety check - if recipient is invalid, don't render
  if (!recipient || !recipient.id || !recipient.phone_number) {
    return null;
  }
  
  // Safety check - ensure lastMessage is valid
  if (!lastMessage || typeof lastMessage !== 'object') {
    return null;
  }
  
  const isUnread = lastMessage && lastMessage.recipient && lastMessage.status === "unread";
  const displayName = recipient.name
    ? recipient.name
    : recipient.phone_number
    ? formatPhoneNumber(
        recipient.phone_number,
        countryCode?.countryCode ? countryCode.countryCode : "US"
      )
    : "No name available";
  
  const initials = recipient.name 
    ? recipient.name.charAt(0).toUpperCase() 
    : recipient.phone_number 
    ? recipient.phone_number.toString().charAt(0) 
    : "?";

  return (
    <>
      {/* WhatsApp-style Avatar */}
      <div className="relative h-12 w-12 flex-none">
        <div className="h-12 w-12 rounded-full bg-neutral-200 flex items-center justify-center overflow-hidden">
          <span className="text-base font-medium text-default-font">
            {initials}
          </span>
        </div>
        {/* Online status indicator can be added here if needed */}
      </div>
      
      {/* Chat Content */}
      <div className="flex grow shrink-0 basis-0 flex-col items-start cursor-pointer select-none min-w-0">
        {/* Top Row: Name and Time */}
        <div className="flex w-full items-center justify-between gap-2 mb-0.5">
          <span
            className={`text-base font-medium text-default-font truncate ${
              isUnread ? "font-semibold" : ""
            }`}
          >
            {displayName}
          </span>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <span
              className={`text-xs whitespace-nowrap ${
                isUnread ? "font-medium text-green-600" : "text-subtext-color"
              }`}
            >
              {lastMessage?.created_at
                ? moment(lastMessage.created_at).isSame(moment(), "day")
                  ? moment(lastMessage.created_at).local().format("hh:mm A")
                  : moment(lastMessage.created_at).local().format("D/MM/YYYY")
                : ""}
            </span>
            {isUnread && (
              <div className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0"></div>
            )}
          </div>
        </div>
        
        {/* Bottom Row: Last Message Preview */}
        <div className="flex w-full items-center justify-between gap-2 min-w-0">
          <div className="flex items-center gap-1.5 min-w-0 flex-1">
            {/* Message status icons for sent messages */}
            {lastMessage && !lastMessage.recipient && (
              <SubframeCore.Icon
                className={`text-sm flex-shrink-0 ${
                  lastMessage?.status === "read" ? "text-blue-500" : "text-subtext-color"
                }`}
                name={
                  (lastMessage?.status === "delivered" ||
                  lastMessage?.status === "read"
                    ? "FeatherCheckCheck"
                    : "FeatherCheck") as IconName
                }
              />
            )}
            <span
              className={`text-sm text-subtext-color truncate ${
                isUnread ? "font-semibold text-default-font" : ""
              }`}
            >
              {lastMessage && lastMessage.system_user &&
                typeof lastMessage.system_user === 'string' &&
                lastMessage.system_user}
              {lastMessage && lastMessage.recipient && 
                typeof lastMessage.recipient === 'string' && 
                lastMessage.recipient}
            </span>
          </div>
          
          {/* Unread count badge (optional, can be added if needed) */}
        </div>
        
        {/* Labels */}
        {(Array.isArray(recipient.global_label) && recipient.global_label.length > 0) ||
         (Array.isArray(recipient.manual_label) && recipient.manual_label.length > 0) ? (
          <div className="flex flex-wrap items-start gap-1 mt-1">
            {Array.isArray(recipient.global_label) &&
              recipient.global_label.map((label: string, index: number) => (
                <span
                  key={`ai-${index}`}
                  className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-500/20 text-default-font border border-amber-500/35"
                >
                  {label}
                </span>
              ))}

            {Array.isArray(recipient.manual_label) &&
              recipient.manual_label.map((label: string, index: number) => (
                <span
                  key={`manual-${index}`}
                  className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-600/22 text-default-font border border-emerald-500/35"
                >
                  {label}
                </span>
              ))}
          </div>
        ) : null}
      </div>
    </>
  );
}

export default Friend;
